/**
 * تجربه‌ی نسخه‌ی مشتری: گفت‌وگو با دستیار.
 *
 * چرا گفت‌وگو و نه فرم چندمرحله‌ای: کاربر نسخه‌ی مشتری عضو هیئت‌مدیره است، نه
 * اپراتور. او یک وضعیت را توصیف می‌کند، پرسش شفاف‌سازی را پاسخ می‌دهد، برآورد
 * را می‌خواند و بعد ادامه می‌دهد: «اگر شرط X عوض شود؟». همین رفت‌وبرگشت، شکل
 * طبیعی گفت‌وگوست و فرم چندمرحله‌ای آن را به کار اداری تبدیل می‌کرد.
 *
 * ⚠️ **نگاشت روی خط لوله، نه یک چت آزاد.** هر پیام کاربر به یکی از سه تماس
 * موجود می‌رود:
 *   - پیام اول → `start`
 *   - پاسخ پرسش‌های شفاف‌سازی → `answer`
 *   - پیام بعدِ برآورد → `refine` (بازسازی با متن تازه)
 * پس هیچ مسیر تازه‌ای در backend لازم نیست و حسابرسی همان است که بود.
 *
 * حالت مشاوره سمت سرور است و کلاینت تنها `consultation_id` را می‌داند؛ رشته‌ی
 * پیام‌ها یک نمای محلی روی همان حالت است.
 */

import { ArrowUp, Bot, Plus, Sparkles, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Badge, IconChip } from '@/components/ui/primitives'
import { resumeConsultation } from '@/lib/api/endpoints'
import { cn } from '@/lib/cn'
import { useMeta } from '@/lib/meta-context'

import { ClarificationBubble } from './ClarificationBubble'
import { VerdictMessage } from './VerdictMessage'
import { useChatConsultation, type ChatMessage } from './useChatConsultation'

export const ChatPage = () => {
  const { meta } = useMeta()
  const chat = useChatConsultation()
  const [draft, setDraft] = useState('')
  const endRef = useRef<HTMLDivElement>(null)
  const [params, setParams] = useSearchParams()

  /*
   * ادامه‌دادن از آرشیو: `?resume=<id>`.
   *
   * پارامتر پس از مصرف پاک می‌شود، وگرنه رفرش صفحه گفت‌وگو را به همان نقطه
   * برمی‌گرداند و پیام‌های تازه‌ی کاربر را دور می‌ریخت.
   *
   * `claimed` نگهبان اجرای دوباره است. در حالت StrictMode توسعه، effect دو بار
   * اجرا می‌شود و بدون این نگهبان دو تماس `resume` می‌رفت.
   */
  const claimed = useRef('')
  const resumeId = params.get('resume') ?? ''

  useEffect(() => {
    if (resumeId === '' || claimed.current === resumeId) {
      return
    }
    claimed.current = resumeId
    void resumeConsultation(resumeId)
      .then((state) => {
        chat.resume(state)
      })
      .catch((error: Error) => {
        toast.error(error.message)
      })
      .finally(() => {
        setParams(
          (current) => {
            const next = new URLSearchParams(current)
            next.delete('resume')
            return next
          },
          { replace: true },
        )
      })
    // `chat` هر رندر تازه ساخته می‌شود؛ گذاشتنش در وابستگی‌ها این effect را
    // بی‌پایان می‌کرد. نگهبان `claimed` تضمین می‌کند یک بار اجرا شود.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId])

  // با هر پیام تازه، انتهای گفت‌وگو در دید می‌آید. این effect فقط DOM را لمس
  // می‌کند و حالتی نمی‌سازد.
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [chat.messages.length, chat.isBusy])

  const send = (): void => {
    const text = draft.trim()
    if (text === '' || chat.isBusy) {
      return
    }
    chat.send(text)
    setDraft('')
  }

  const isEmpty = chat.messages.length === 0

  return (
    <div className="flex min-h-[calc(100dvh-8rem)] flex-col">
      {/* ---------------- سربرگ گفت‌وگو ---------------- */}
      <header className="flex flex-wrap items-center justify-between gap-3 pb-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <IconChip tone="solid" size="lg">
            <Sparkles className="size-5.5" aria-hidden />
          </IconChip>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-bold tracking-tight text-ink sm:text-xl">
              {meta.profile.app_title}
            </h1>
            <p className="mt-0.5 truncate text-xs text-ink-muted">
              {meta.profile.subtitle === ''
                ? `برآورد بر پایه‌ی منطق تصمیم ${meta.profile.principal_name}`
                : meta.profile.subtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {chat.stage !== null && (
            <Badge tone={chat.stage === 'completed' ? 'support' : 'accent'} dot>
              {chat.stage === 'completed' ? 'برآورد آماده است' : 'در جریان'}
            </Badge>
          )}
          {!isEmpty && (
            <Button
              variant="secondary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => {
                chat.reset()
                setDraft('')
              }}
            >
              گفت‌وگوی تازه
            </Button>
          )}
        </div>
      </header>

      {/* ---------------- رشته‌ی پیام‌ها ---------------- */}
      <div className="flex-1 space-y-5">
        {isEmpty ? (
          <Welcome
            onPick={(text) => {
              setDraft(text)
            }}
          />
        ) : (
          chat.messages.map((message) => (
            <Message key={message.id} message={message} chat={chat} />
          ))
        )}

        {chat.isBusy && <Thinking label={chat.busyLabel} />}
        <div ref={endRef} />
      </div>

      {/* ---------------- نوشتن پیام ---------------- */}
      <div className="sticky bottom-0 -mx-4 mt-6 px-4 pb-4 sm:-mx-6 sm:px-6">
        {/* محو شدن تدریجی پشت نوار نوشتن، تا متن زیرش قطع‌شده دیده نشود. */}
        <div
          className="pointer-events-none absolute inset-x-0 -top-8 h-8 bg-linear-to-t from-canvas to-transparent"
          aria-hidden
        />
        <form
          className="relative rounded-lg border border-line bg-surface-raised p-2 shadow-lifted"
          onSubmit={(event) => {
            event.preventDefault()
            send()
          }}
        >
          <label htmlFor="chat-input" className="sr-only">
            پیام شما
          </label>
          <textarea
            id="chat-input"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
            }}
            onKeyDown={(event) => {
              // Enter می‌فرستد و Shift+Enter خط تازه می‌سازد — قرارداد آشنای
              // هر رابط گفت‌وگو.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                send()
              }
            }}
            rows={draft.split('\n').length > 3 ? 6 : 3}
            placeholder={
              chat.stage === 'completed'
                ? 'می‌توانید فرض‌ها را عوض کنید و برآورد تازه بگیرید…'
                : meta.profile.input_placeholder
            }
            className="w-full resize-none bg-transparent px-3 py-2.5 text-sm leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none"
          />
          <div className="flex items-center justify-between gap-3 px-1.5 pb-0.5">
            <p className="text-[11px] leading-relaxed text-ink-faint">
              {chat.stage === 'completed'
                ? 'پیام تازه، برآورد را با فرض‌های جدید بازسازی می‌کند.'
                : 'Enter برای ارسال، Shift+Enter برای خط تازه.'}
            </p>
            <Button
              type="submit"
              variant="primary"
              size="icon"
              aria-label="ارسال"
              loading={chat.isBusy}
              disabled={draft.trim() === ''}
            >
              <ArrowUp className="size-4.5" aria-hidden />
            </Button>
          </div>
        </form>
        <p className="mt-2 text-center text-[11px] leading-relaxed text-ink-faint">
          {meta.profile.disclaimer}
        </p>
      </div>
    </div>
  )
}

// ===================================================================
// پیام‌ها
// ===================================================================

const Message = ({
  message,
  chat,
}: {
  message: ChatMessage
  chat: ReturnType<typeof useChatConsultation>
}) => {
  if (message.kind === 'user') {
    return (
      <div className="animate-rise flex justify-end gap-3">
        <div className="max-w-[85%] rounded-lg rounded-se-sm bg-linear-100 from-accent to-accent-two px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap text-white shadow-glow">
          {message.text}
        </div>
        <IconChip tone="neutral" size="sm" className="mt-0.5">
          <User className="size-3.5" />
        </IconChip>
      </div>
    )
  }

  return (
    <div className="animate-rise flex gap-3">
      <IconChip tone="accent" size="sm" className="mt-0.5">
        <Bot className="size-3.5" />
      </IconChip>
      <div className="min-w-0 flex-1">
        {message.kind === 'note' && (
          <p className="rounded-lg rounded-ss-sm border border-line bg-surface-raised px-4 py-3 text-sm leading-relaxed text-ink shadow-raised">
            {message.text}
          </p>
        )}
        {message.kind === 'clarification' && (
          <ClarificationBubble
            questions={message.questions}
            isBusy={chat.isBusy}
            answered={message.answered}
            prefill={message.prefill}
            carriedCount={message.carriedCount}
            onSubmit={chat.answerClarification}
          />
        )}
        {message.kind === 'verdict' && (
          <VerdictMessage
            state={message.state}
            onFeedback={chat.giveFeedback}
            isLatest={message.isLatest}
          />
        )}
      </div>
    </div>
  )
}

/** نشانگر «در حال فکر کردن». متنش می‌گوید سرور مشغول چه کاری است. */
const Thinking = ({ label }: { label: string }) => (
  <div className="animate-fade flex gap-3" role="status">
    <IconChip tone="accent" size="sm" className="mt-0.5">
      <Bot className="size-3.5" />
    </IconChip>
    <div className="flex items-center gap-3 rounded-lg rounded-ss-sm border border-line bg-surface-raised px-4 py-3 shadow-raised">
      <span className="flex gap-1" aria-hidden>
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="animate-breathe size-1.5 rounded-full bg-accent"
            style={{ animationDelay: `${String(index * 220)}ms` }}
          />
        ))}
      </span>
      <span className="text-xs text-ink-muted">{label}</span>
    </div>
  </div>
)

// ===================================================================
// صفحه‌ی شروع
// ===================================================================

const Welcome = ({ onPick }: { onPick: (text: string) => void }) => {
  const { meta } = useMeta()
  const examples = meta.profile.example_cases

  return (
    <div className="animate-rise flex flex-col items-center py-10 text-center">
      <IconChip tone="solid" size="lg" className="size-14">
        <Sparkles className="size-7" aria-hidden />
      </IconChip>
      <h2 className="mt-5 text-xl font-bold tracking-tight text-ink sm:text-2xl">
        تصمیم‌تان را توصیف کنید
      </h2>
      <p className="mt-2.5 max-w-lg text-sm leading-relaxed text-ink-muted">
        مسئله، گزینه‌ها، محدودیت‌ها و نتیجه‌ی مطلوب را آزاد و به فارسی بنویسید. دستیار
        تصمیم‌های مشابه گذشته را پیدا می‌کند و یک برآورد ساختاریافته می‌دهد.
      </p>

      {examples.length > 0 && (
        <div className="mt-8 w-full max-w-2xl">
          <p className="text-[11px] font-semibold tracking-wide text-ink-faint">
            یا از یک نمونه شروع کنید
          </p>
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {examples.map((example) => (
              <button
                key={example.label}
                type="button"
                onClick={() => {
                  onPick(example.text)
                }}
                className={cn(
                  'lift group rounded-md border border-line bg-surface-raised p-4 text-start shadow-raised',
                  'hover:border-accent-line',
                )}
              >
                <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                  <Sparkles
                    className="size-3.5 text-accent transition-transform group-hover:scale-110"
                    aria-hidden
                  />
                  {example.label}
                </p>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-ink-muted">
                  {example.text}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
